import { FC, useEffect, useState } from 'react';

import User from '@/assets/user.svg?react';

import { useFormDataQuery } from '@/api/getters';
import { BUSINESS_TYPE } from '@/api/types';
import Home from '@/assets/home.svg?react';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import InputAddress from '@/features/v2/summaryForms/components/InputAdress';
import InputBuildingNumber from '@/features/v2/summaryForms/components/InputBuildingNumber';
import InputCity from '@/features/v2/summaryForms/components/InputCity';
import InputFlatNumber from '@/features/v2/summaryForms/components/InputFlatNumber';
import InputKRS from '@/features/v2/summaryForms/components/InputKRS';
import InputNipCompany from '@/features/v2/summaryForms/components/InputNip';
import InputPostalCode from '@/features/v2/summaryForms/components/InputPostalCode';
import InputRegon from '@/features/v2/summaryForms/components/InputRegon';

import { validateCompanyName } from '../../Validate';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

interface Props {
  prefix?: 'owner' | `coowners.${number}`;
  businessType?: string;
}

export const OWNER_COMPANY_NAME = 'owner.company_name';
export const OWNER_TAX_OFFICE_NAME = 'owner.tax_office';

const CompaniesCompanyInputs: FC<Props> = ({
  prefix = 'owner',
  businessType,
}) => {
  const { trigger } = useFormContext();
  const { i18n } = useLingui();

  const [focused, setFocused] = useState('');

  const { data } = useFormDataQuery();

  useEffect(() => {
    const fields = [
      'company_name',
      'nip_company',
      'krs',
      'regon',
      'address_company',
      'building_number_company',
      'flat_number_company',
      'zip_code_company',
      'city_company',
    ];
    businessType === BUSINESS_TYPE.ZOO && fields.push('regon');

    fields.forEach((field) => {
      if (data?.payload?.data?.owner) {
        if (data?.payload?.data?.owner?.[field]?.length > 0) {
          trigger(`owner.${field}`);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      className={
        'w-full max-w-full space-y-3 rounded-[10px] border-t-[6px] border-blue-600 bg-gray-100 px-3.5 py-4 md:space-y-3 md:rounded-lg md:p-6'
      }
    >
      <h4>{i18n.t('Podaj dane spółki')}</h4>
      <div
        className={'flex flex-col gap-5 rounded-md bg-gray-200 p-3.5 md:px-6'}
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <p className="h5 flex items-center gap-2 md:col-span-4">
            <User className="size-4 text-blue-600" />
            {i18n.t('Spółka')}
          </p>
          <FormField
            name={`${prefix}.company_name`}
            rules={{
              validate: (value) => validateCompanyName(value),
            }}
            render={({ field, fieldState }) => {
              const { onChange, onBlur, ...restField } = field;
              return (
                <FormItem className="col-span-4 md:col-span-4">
                  <FormControl>
                    <Input
                      label={i18n.t('Nazwa spółki')}
                      error={fieldState.error?.message}
                      success={
                        focused !== field.name &&
                        !fieldState.error &&
                        field.value
                      }
                      onChange={(e) => {
                        onChange(e);
                        if (fieldState.error) {
                          trigger(field.name);
                        }
                      }}
                      onBlur={() => {
                        setFocused('');
                        onBlur();
                      }}
                      onFocus={() => setFocused(field.name)}
                      {...restField}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
          <InputNipCompany
            name={`${prefix}.nip_company`}
            focused={focused}
            setFocused={setFocused}
          />
          <InputKRS
            name={`${prefix}.krs`}
            focused={focused}
            setFocused={setFocused}
          />
          <InputRegon
            name={`${prefix}.regon`}
            focused={focused}
            setFocused={setFocused}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <p className="h5 flex items-center gap-2 md:col-span-4">
            <Home className="size-4 text-blue-600" />
            {i18n.t('Adres spółki')}
          </p>
          <InputPostalCode
            name={`${prefix}.zip_code_company`}
            focused={focused}
            setFocused={setFocused}
            prefix={prefix}
          />
          <InputCity
            name={`${prefix}.city_company`}
            focused={focused}
            setFocused={setFocused}
            prefix={prefix}
          />
          <InputAddress
            name={`${prefix}.address_company`}
            focused={focused}
            setFocused={setFocused}
            prefix={prefix}
          />
          <div className="col-span-4 grid grid-cols-4 gap-3">
            <InputBuildingNumber
              name={`${prefix}.building_number_company`}
              focused={focused}
              setFocused={setFocused}
            />
            <InputFlatNumber
              name={`${prefix}.flat_number_company`}
              focused={focused}
              setFocused={setFocused}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompaniesCompanyInputs;
